// frontend/src/hooks/useStoreToday.js
//
// "What is today, where the STORE is?" — the anchor for every business date
// the SaaS renders or sends, such as an overdue comparison against an
// order-by or need-by date.
//
// It is the store's day, not the viewer's, because that is the day the server
// works in: `store.get_local_time().date()` dates every goods receipt
// (backend/purchase_orders/po_viewset.py `receive_goods`), and
// `core.date_utils.classify_due_status` decides overdue against the same
// clock. A comparison made here in the viewer's zone would be a SECOND clock,
// and the two disagree for hours every day.
//
// The zone rides on `selectedStore.timezone`, part of the one client-side
// Store shape (backend/authentication/store_payload.py), so it is already in
// the auth context on every page — no request, no loading state of its own.
//
// NO FALLBACK. Before the store resolves, the answer is '' — never the
// browser's day. Callers render nothing and send nothing on '', which lets the
// server apply the store-local default it computes anyway. An unknown zone has
// no right answer; a plausible wrong date is worse than a blank.

import { useAuth } from '../contexts/AuthContext';
import { todayInTimeZone } from '../utils/formatters';

/**
 * @returns {string} today in the selected store's IANA zone as 'YYYY-MM-DD',
 *   or '' while no store is resolved.
 */
export function useStoreToday() {
  const { selectedStore } = useAuth();
  // Recomputed each render on purpose: a page left open across store-local
  // midnight should move to the new day, not hold the day it was opened.
  return todayInTimeZone(selectedStore?.timezone);
}

export default useStoreToday;
